import { useId, useRef, useState } from "react";
import { ApiError, uploadFileWithAuth } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useSiteCopy } from "../context/SiteCopyContext";
import { getAtPath } from "../lib/siteCopyPaths";

type Props = {
  path: string;
  placeholderLabel: string;
  altPath?: string;
  className?: string;
  formIdentifier?: string;
};

export function CopyImage({
  path,
  placeholderLabel,
  altPath,
  className = "photo-ph",
  formIdentifier = "admin-dorothy-portrait",
}: Props) {
  const patternId = useId().replace(/:/g, "");
  const inputRef = useRef<HTMLInputElement>(null);
  const { copy, savePath } = useSiteCopy();
  const { isAdmin } = useAuth();
  const url = String(getAtPath(copy, path) ?? "").trim();
  const alt = altPath ? String(getAtPath(copy, altPath) ?? "") : "";
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const hasImage = url.length > 0;

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploading(true);
    setErr(null);
    try {
      const data = await uploadFileWithAuth(file, { formIdentifier });
      if (!data.mimetype.startsWith("image/")) {
        setErr("Please choose an image file.");
        return;
      }
      await savePath(path, data.uploadsUrl);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="copy-image-root">
      <div
        className={`${className}${hasImage ? " photo-ph--has-image" : ""}`}
        data-label={placeholderLabel}
      >
        {hasImage ? (
          <img src={url} alt={alt} />
        ) : (
          <svg viewBox="0 0 10 10" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <pattern id={patternId} width="1" height="1" patternUnits="userSpaceOnUse">
                <line x1="0" y1="0" x2="0" y2="1" stroke="currentColor" strokeWidth="0.04" />
              </pattern>
            </defs>
            <rect width="10" height="10" fill={`url(#${patternId})`} />
          </svg>
        )}
        {isAdmin && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
              className="copy-image-file"
              onChange={(ev) => void onPickFile(ev)}
              disabled={uploading}
            />
            <button
              type="button"
              className="copy-image-edit"
              aria-label={`Upload image: ${path}`}
              title={uploading ? "Uploading…" : "Upload image (admin)"}
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
            >
              ✎
            </button>
          </>
        )}
      </div>
      {isAdmin && err && <p className="copy-image-error">{err}</p>}
    </div>
  );
}
